import { useContext, useMemo } from 'react';

import { OptimizationContainer } from '@cast/types';

import { getContainerResourceRecommendations } from 'features/workload-optimization';

import { predictionMetricKeys, ResourceMetricKey } from './_constants';
import { prepareCpuPredictionChartMetrics } from './_utils';
import { useDisabledMetricsBoxes } from '../../../_hooks/useDisabledMetricsBoxes';
import { useRightsizingTimeRangeSelection } from '../../../_hooks/useRightsizingTimeRangeSelection';
import { WorkloadDetailsContext } from '../../../workload-details/_context';

export const useCpuPredictionChartData = ({
  container,
  timeZone: timeZoneProp,
}: {
  container?: OptimizationContainer;
  timeZone?: string;
}) => {
  const { timeZone: rightsizingTimeZone } = useRightsizingTimeRangeSelection();
  const timeZone = timeZoneProp ?? rightsizingTimeZone;
  const { workload, metrics, isLoading, isFetching, error, refetch } =
    useContext(WorkloadDetailsContext);
  const { toggleMetric, isMetricDisabled } =
    useDisabledMetricsBoxes<ResourceMetricKey>();

  const containerMetrics = useMemo(
    () => metrics?.containers.find(({ name }) => name === container?.name),
    [metrics?.containers, container?.name]
  );

  const resourceRecommendations = useMemo(
    () => (container ? getContainerResourceRecommendations(container) : null),
    [container]
  );

  const chartData = useMemo(() => {
    if (!workload || !container || !resourceRecommendations) {
      return null;
    }

    const { cpu } = resourceRecommendations;

    return {
      chartData: prepareCpuPredictionChartMetrics(containerMetrics?.cpuCores),
      chartDataKeys: predictionMetricKeys,
      usageSummaryWithPredictions: containerMetrics?.cpuCoresAggregated && {
        ...containerMetrics.cpuCoresAggregated,
        avgPredicted:
          containerMetrics.cpuCoresPredictionsAggregated?.avg ?? null,
      },
      predictionsAggregated: containerMetrics?.cpuCoresPredictionsAggregated,
      recommended: cpu?.recommended,
      isReadOnly: workload.cpu?.isReadOnly,
    };
  }, [workload, container, containerMetrics, resourceRecommendations]);

  return {
    workload,
    timeZone,
    isLoading,
    isFetching,
    error,
    refetch,
    chartData,
    toggleMetric,
    isMetricDisabled,
  };
};
